import { CommonModule } from '@angular/common';
import {
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnChanges,
	Output,
	SimpleChanges,
	ViewChild,
	inject,
} from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { DropdownBehaviourDirective } from 'src/app/util/directives/dropdownBehaviour/dropdown-behaviour.directive';

export interface MultiCheckboxSelectOption {
	value: string;
	label: string;
	group_name: string;
	emphasized?: boolean;
	parent?: MultiCheckboxSelectOption;
	children?: MultiCheckboxSelectOption[];
	selected: boolean;
}

@Component({
	selector: 'msp-multi-checkbox-select',
	standalone: true,
	imports: [CommonModule, FontAwesomeModule, DropdownBehaviourDirective],
	templateUrl: './msp-multi-checkbox-select.component.html',
	styleUrls: ['./msp-multi-checkbox-select.component.scss'],
})
export class MspMultiCheckboxSelectComponent implements OnChanges {
	private readonly elementRef = inject(ElementRef<HTMLElement>);

	@ViewChild('dropdownPanel') dropdownPanelRef?: ElementRef<HTMLElement>;

	@Input() options: MultiCheckboxSelectOption[] = [];
	selectedValues: MultiCheckboxSelectOption[] = [];
	@Input() allLabel = 'Todos';
	@Input() selectedLabel = 'selecionados';
	@Input() maxDropdownHeight = '300px';
	@Input() minWidth = '250px';
	@Input() disabled = false;
	@Input() cleanValues = false;

	@Output() selectedValuesChange = new EventEmitter<MultiCheckboxSelectOption[]>();

	isDropdownOpen = false;

	icon = {
		faAngleDown: faAngleDown,
	};

	ngOnChanges(changes: SimpleChanges): void {
		if (changes?.cleanValues && changes.cleanValues.currentValue === true) {
			this.clearSelection();
		}
	}

	protected toggleDropdown(): void {
		if (this.disabled) return;
		// no stopPropagation here: the click must bubble to document so other open
		// dropdown instances see it and close themselves (onDocumentClick already
		// guards against this instance closing itself via clickedInsideTrigger)
		this.isDropdownOpen = !this.isDropdownOpen;
	}

	public clearSelection(event?: Event): void {
		event?.stopPropagation();
		this.traverseOptionsAndUnselectAll(this.options);
		this.selectedValues = [];
		this.selectedValuesChange.emit([]);
	}

	protected toggleSelection(value: MultiCheckboxSelectOption, event?: Event): void {
		event?.stopPropagation();
		value.selected = !value.selected;
		this.selectedValues = this.traverseOptionsAndGetSelected(this.options, () => {});
		this.selectedValuesChange.emit(this.selectedValues);
	}

	traverseOptionsAndUnselectAll(options: MultiCheckboxSelectOption[]): void {
		for (const option of options) {
			option.selected = false;
			if (option.children && option.children.length > 0) {
				this.traverseOptionsAndUnselectAll(option.children);
			}
		}
	}

	traverseOptionsAndGetSelected(
		options: MultiCheckboxSelectOption[],
		callback: (option: MultiCheckboxSelectOption) => void
	): MultiCheckboxSelectOption[] {
		const selectedOptions: MultiCheckboxSelectOption[] = [];
		for (const option of options) {
			callback(option);
			if (option.children && option.children.length > 0) {
				selectedOptions.push(...this.traverseOptionsAndGetSelected(option.children, callback));
			}

			if (option.selected) {
				selectedOptions.push(option);
			}
		}

		return selectedOptions;
	}

	protected getSelectionLabel(): string {
		if (this.selectedValues.length === 0) {
			return this.allLabel;
		}

		if (this.selectedValues.length === 1) {
			return this.selectedValues[0].label;
		}

		return `${this.selectedValues.length} ${this.selectedLabel}`;
	}

	@HostListener('document:click', ['$event'])
	protected onDocumentClick(event: Event): void {
		const target = event.target as Node;
		const clickedInsideTrigger = this.elementRef.nativeElement.contains(target);
		const clickedInsidePanel = this.dropdownPanelRef?.nativeElement.contains(target) ?? false;

		if (!clickedInsideTrigger && !clickedInsidePanel) {
			this.isDropdownOpen = false;
		}
	}
}
